"use client";

import { useState, useCallback } from "react";
import Link from "next/link";
import { X, Heart, GitCompareArrows, MessageCircle, Sparkles } from "lucide-react";
import { authRedirectLink } from "@/lib/url";

interface LoginPromptModalProps {
  open: boolean;
  onClose: () => void;
  trigger?: "favorite" | "compare" | "chat" | "recommend";
}

const TRIGGER_INFO = {
  favorite: {
    icon: Heart,
    title: "즐겨찾기",
    desc: "관심 시술을 저장하고 변경사항을 알림 받으세요",
  },
  compare: {
    icon: GitCompareArrows,
    title: "비교 저장",
    desc: "시술 비교 결과를 저장하고 나중에 확인하세요",
  },
  chat: { icon: MessageCircle, title: "AI 상담", desc: "대화 기록을 저장하고 이어서 상담하세요" },
  recommend: { icon: Sparkles, title: "맞춤 추천", desc: "내 관심사 기반 시술 추천을 받으세요" },
};

const BENEFITS = ["시술 즐겨찾기 & 알림", "비교 결과 저장", "AI 상담 대화 기록", "맞춤 시술 추천"];

export function LoginPromptModal({ open, onClose, trigger = "favorite" }: LoginPromptModalProps) {
  if (!open) return null;

  const info = TRIGGER_INFO[trigger];
  const Icon = info.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="animate-in fade-in zoom-in-95 relative w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl duration-200">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="gradient-primary-soft mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl">
          <Icon className="h-7 w-7 text-violet-600" />
        </div>

        <h3 className="mb-1 text-center text-lg font-bold tracking-tight">
          가입하고 {info.title} 이용하기
        </h3>
        <p className="text-muted-foreground mb-5 text-center text-sm">{info.desc}</p>

        <ul className="mb-6 space-y-2">
          {BENEFITS.map((b) => (
            <li key={b} className="flex items-center gap-2 text-sm">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-100 text-[10px] font-bold text-green-600">
                ✓
              </span>
              {b}
            </li>
          ))}
        </ul>

        <Link
          href={authRedirectLink(
            "signup",
            typeof window !== "undefined" ? window.location.pathname : "/wiki",
          )}
          className="gradient-cta block w-full rounded-2xl py-3.5 text-center text-base font-bold text-white shadow-lg shadow-violet-500/20 transition-all hover:-translate-y-0.5"
          onClick={onClose}
        >
          시작하기
        </Link>

        <p className="text-muted-foreground mt-3 text-center text-xs">
          이미 계정이 있나요?{" "}
          <Link
            href={authRedirectLink(
              "signin",
              typeof window !== "undefined" ? window.location.pathname : "/wiki",
            )}
            className="font-semibold text-violet-600 hover:underline"
          >
            로그인
          </Link>
        </p>

        <button
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground mt-2 block w-full text-center text-xs"
        >
          나중에 하기
        </button>
      </div>
    </div>
  );
}

export function useLoginPrompt() {
  const [modalState, setModalState] = useState<{
    open: boolean;
    trigger: LoginPromptModalProps["trigger"];
  }>({
    open: false,
    trigger: "favorite",
  });

  const requireLogin = useCallback(
    (isLoggedIn: boolean, trigger: LoginPromptModalProps["trigger"], onAuthed: () => void) => {
      if (isLoggedIn) {
        onAuthed();
      } else {
        setModalState({ open: true, trigger });
      }
    },
    [],
  );

  const closeModal = useCallback(() => {
    setModalState((s) => ({ ...s, open: false }));
  }, []);

  return { modalState, requireLogin, closeModal };
}
